import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RichTextEditor, RichTextEditorRef } from "@/components/Editor/RichTextEditor";
import { emailAutomationService, EmailTemplateData } from "@/services/emailAutomation.service";
import { toast } from "react-hot-toast";
import SkeletonCard from "@/components/SkeletonCard";
import { useNavigate } from "react-router-dom";
import { checkRichTextLength } from "@/utils/richText";

interface EmailTemplateEditorProps {
  isOpen: boolean;
  onClose: () => void;
  emailType: string;
  onSave?: () => void;
}

interface AvailableVariable {
  variable: string;
  description: string;
}

export function EmailTemplateEditor({
  isOpen,
  onClose,
  emailType,
  onSave,
}: EmailTemplateEditorProps) {
  const navigate = useNavigate();
  const [template, setTemplate] = useState<EmailTemplateData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
    body: "",
  });
  const [selectedVariable, setSelectedVariable] = useState<string | null>(null);
  const editorRef = useRef<RichTextEditorRef>(null);

  const emailTypeLabels: Record<string, string> = {
    welcome: "welcome new ambassador",
    newSale: "new sale",
    payout: "payout sent",
    approval: "affiliate approval",
    invite: "account invite",
  };

  // Available variables for each email type with descriptions
  // These match the variables used in the backend service
  const availableVariables: Record<string, AvailableVariable[]> = {
    welcome: [
      { variable: "{{brandName}}", description: "The company or brand name" },
      { variable: "{{affiliateFirstName}}", description: "The affiliate's first name" },
      { variable: "{{affiliateLastName}}", description: "The affiliate's last name" },
    ],
    newSale: [
      { variable: "{{brandName}}", description: "The company or brand name" },
      { variable: "{{affiliateFirstName}}", description: "The affiliate's first name" },
      { variable: "{{affiliateLastName}}", description: "The affiliate's last name" },
      { variable: "{{totalAmount}}", description: "The total amount of the order" },
      { variable: "{{commissionAmount}}", description: "The commission earned from this sale" },
      { variable: "{{orderNumber}}", description: "The order number or reference" },
    ],
    payout: [
      { variable: "{{brandName}}", description: "The company or brand name" },
      { variable: "{{affiliateFirstName}}", description: "The affiliate's first name" },
      { variable: "{{affiliateLastName}}", description: "The affiliate's last name" },
      { variable: "{{amount}}", description: "The payout amount sent" },
      { variable: "{{paymentMethod}}", description: "The payment method used (bank transfer, PayPal, etc.)" },
      { variable: "{{referenceNumber}}", description: "The reference number for the payout" },
    ],
    approval: [
      { variable: "{{brandName}}", description: "The company or brand name" },
      { variable: "{{affiliateFirstName}}", description: "The affiliate's first name" },
      { variable: "{{affiliateLastName}}", description: "The affiliate's last name" },
      { variable: "{{commissionAmount}}", description: "The commission rate for the affiliate" },
      { variable: "{{discountAmount}}", description: "The discount rate for the affiliate" },
    ],
    invite: [
      { variable: "{{brandName}}", description: "The company or brand name" },
      { variable: "{{affiliateFirstName}}", description: "The affiliate's first name" },
      { variable: "{{affiliateLastName}}", description: "The affiliate's last name" },
    ],
  };

  // Load template data when dialog opens
  useEffect(() => {
    if (isOpen) {
      loadTemplate();
    }
  }, [isOpen, emailType]);

  const loadTemplate = async () => {
    setIsLoading(true);
    try {
      const data = await emailAutomationService.getEmailTemplate(emailType);
      setTemplate(data);
      setFormData({
        subject: data.subject,
        body: data.body,
      });
    } catch (error: any) {
      console.error("Error loading email template:", error);
      // Check if it's a 403 error (permission denied)
      if (error?.response?.status === 403) {
        toast.error("This feature is not available on your plan. Please upgrade to access this feature.");
        onClose();
        navigate("/brand/pricing?upgradeRequired=true&feature=NON_WELCOME_EMAIL_AUTOMATION");
      } else {
        toast.error("Failed to load email template");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate form data
    const validation = emailAutomationService.validateEmailTemplate(formData);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    setIsSaving(true);
    try {
      await emailAutomationService.updateEmailTemplate(emailType, formData);
      toast.success("Email template updated successfully");
      onSave?.();
      onClose();
    } catch (error: any) {
      console.error("Error saving email template:", error);
      // Check if it's a 403 error (permission denied)
      if (error?.response?.status === 403) {
        toast.error("This feature is not available on your plan. Please upgrade to access this feature.");
        onClose();
        navigate("/brand/pricing?upgradeRequired=true&feature=NON_WELCOME_EMAIL_AUTOMATION");
      } else {
        toast.error("Failed to save email template");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original template
    if (template) {
      setFormData({
        subject: template.subject,
        body: template.body,
      });
    }
    onClose();
  };

  const maxCharacters = 3000;
  const { isOver: isOverLimit } = checkRichTextLength(formData.body, maxCharacters);

  // Handle variable click for body (insert into editor)
  const handleVariableClick = (variable: string) => {
    setSelectedVariable(variable);
    // Try to insert text using the editor ref if available
    if (editorRef.current) {
      editorRef.current.insertText(variable);
    } else {
      // Fallback: append to body content
      setFormData(prev => ({
        ...prev,
        body: prev.body ? prev.body + ` ${variable}` : variable,
      }));
    }
  };

  // Handle variable click for subject line
  const handleSubjectVariableClick = (variable: string) => {
    setFormData(prev => ({
      ...prev,
      subject: prev.subject ? prev.subject + ` ${variable}` : variable,
    }));
  };

  // Replace placeholders with sample data for preview
  const getReplacements = () => ({
    "{{brandName}}": "Gymshark",
    "{{affiliateFirstName}}": "John",
    "{{affiliateLastName}}": "Doe",
    "{{totalAmount}}": "$125.00",
    "{{commissionAmount}}": "$12.50",
    "{{orderNumber}}": "ORD-12345",
    "{{amount}}": "$500.00",
    "{{paymentMethod}}": "Wise",
    "{{referenceNumber}}": "REF-78901",
    "{{discountAmount}}": "10%",
  });

  const getPreviewContent = () => {
    let preview = formData.body || "";
    const replacements = getReplacements();

    Object.entries(replacements).forEach(([placeholder, value]) => {
      preview = preview.replace(new RegExp(placeholder, 'g'), value);
    });

    return preview;
  };

  // Replace placeholders in subject for preview
  const getPreviewSubject = () => {
    let subject = formData.subject || "Email subject will appear here";
    const replacements = getReplacements();

    Object.entries(replacements).forEach(([placeholder, value]) => {
      subject = subject.replace(new RegExp(placeholder, 'g'), value);
    });

    return subject;
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <DialogHeader>
            Edit Template: {emailTypeLabels[emailType]}
          </DialogHeader>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Email Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject" className="text-sm font-medium text-muted-foreground">
                Email Subject
              </Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, subject: e.target.value }))
                }
                placeholder="Enter email subject"
                maxLength={500}
                className="w-full"
              />
              {/* Quick insert variables into subject */}
              <div className="flex flex-wrap gap-1 mt-1">
                {availableVariables[emailType]?.slice(0, 3).map((item) => (
                  <button
                    key={item.variable}
                    type="button"
                    onClick={() => handleSubjectVariableClick(item.variable)}
                    className="px-2 py-0.5 text-xs bg-brand-50 text-brand-700 rounded hover:bg-brand-100 transition-colors border border-brand-200"
                    title={`Insert ${item.variable} in subject`}
                  >
                    {item.variable}
                  </button>
                ))}
              </div>
            </div>

            {/* Available Variables */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">
                Available Variables
              </Label>
              <div className="flex flex-wrap gap-2">
                {availableVariables[emailType]?.map((item) => (
                  <button
                    key={item.variable}
                    type="button"
                    onClick={() => handleVariableClick(item.variable)}
                    onMouseEnter={() => setSelectedVariable(item.variable)}
                    onMouseLeave={() => setSelectedVariable(null)}
                    className="px-3 py-1.5 text-sm bg-muted text-muted-foreground rounded-lg hover:bg-accent transition-colors border border-border"
                    title={item.description}
                  >
                    {item.variable}
                  </button>
                ))}
              </div>
              {/* Show description on hover */}
              {selectedVariable && (
                <p className="text-xs text-muted-foreground mt-1">
                  {availableVariables[emailType]?.find(v => v.variable === selectedVariable)?.description}
                </p>
              )}
            </div>

            {/* Email Body */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">
                Email Body
              </Label>
              <div className={isOverLimit ? "border-2 border-red-500 rounded-lg" : ""}>
                <RichTextEditor
                  ref={editorRef}
                  content={formData.body}
                  onChange={(content) =>
                    setFormData((prev) => ({ ...prev, body: content }))
                  }
                  placeholder="Enter email body"
                  maxLength={3000}
                  limitVariant="email"
                />
              </div>
            </div>

            {/* Preview Section */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">
                Preview
              </Label>
              <div className="p-4 bg-muted rounded-lg min-h-[150px] border border-border">
                <div className="font-medium mb-2 text-foreground">
                  {getPreviewSubject()}
                </div>
                <div 
                  className="prose max-w-none text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: getPreviewContent() }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSaving}
                className="border-border"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSaving || isOverLimit}
              >
                {isSaving ? "Saving..." : "Save Template"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </DialogRoot>
  );
}
